import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Lock,
  RefreshCw,
  Users,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  LogOut,
  BarChart3,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "atlas_admin_basic";
const PALETTE = ["#6B46C1", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"];
const WINDOWS = [
  { days: 7, label: "7 días" },
  { days: 30, label: "30 días" },
  { days: 90, label: "90 días" },
];

function buildAuth(u, p) {
  return "Basic " + btoa(`${u}:${p}`);
}

export default function AdminMetrics() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const fetchMetrics = useCallback(
    async (header, d) => {
      const authHeader = header || auth;
      const win = d || days;
      if (!authHeader) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/metrics?days=${win}`, {
          headers: { Authorization: authHeader },
        });
        setMetrics(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          try {
            sessionStorage.removeItem(STORAGE_KEY);
          } catch {
            // ignore
          }
          setAuth(null);
          toast.error("Sesión expirada");
        } else {
          toast.error("No se pudieron cargar las métricas");
        }
      } finally {
        setLoading(false);
      }
    },
    [auth, days],
  );

  useEffect(() => {
    if (auth) fetchMetrics(auth, days);
  }, [auth, days, fetchMetrics]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username.trim() || !loginForm.password) return;
    setLoggingIn(true);
    const header = buildAuth(loginForm.username.trim(), loginForm.password);
    try {
      await axios.get(`${API}/admin/check`, {
        headers: { Authorization: header },
      });
      try {
        sessionStorage.setItem(STORAGE_KEY, header);
      } catch {
        // ignore
      }
      setAuth(header);
      toast.success("Bienvenido al panel ATLAS");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Credenciales inválidas");
      } else {
        toast.error("No se pudo conectar con el servidor");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setAuth(null);
    setMetrics(null);
  };

  if (!auth) {
    return <LoginCard form={loginForm} setForm={setLoginForm} onSubmit={handleLogin} loading={loggingIn} />;
  }

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      data-testid="admin-metrics-dashboard"
    >
      <AdminHeader current="metrics" onLogout={handleLogout} />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold">
              Métricas de Leads
            </h1>
            <p className="text-sm text-slate-500">
              Performance comercial en tiempo real, por canal, idioma y provincia.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="inline-flex rounded-lg border border-slate-200 bg-white p-1"
              data-testid="metrics-window-switcher"
            >
              {WINDOWS.map((w) => (
                <button
                  key={w.days}
                  onClick={() => setDays(w.days)}
                  data-testid={`metrics-window-${w.days}`}
                  className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                    days === w.days
                      ? "bg-[#6B46C1] text-white"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <Button
              data-testid="metrics-refresh-btn"
              variant="outline"
              size="sm"
              onClick={() => fetchMetrics()}
              disabled={loading}
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {metrics ? (
          <MetricsContent metrics={metrics} />
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500">
            <Loader2 className="inline h-6 w-6 animate-spin mr-2" />
            Cargando métricas...
          </div>
        )}
      </main>
    </div>
  );
}

function MetricsContent({ metrics }) {
  const { totals, daily_series, by_language, by_utm_source, by_provincia, by_utm_medium, by_utm_campaign } =
    metrics;

  const positive = totals.delta_pct == null ? null : totals.delta_pct >= 0;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total"
          label="Leads (período)"
          value={totals.current}
          icon={Users}
          accent="from-purple-500/20 to-purple-500/5 text-[#6B46C1]"
        />
        <KpiCard
          testid="kpi-delta"
          label="vs. período anterior"
          value={totals.delta_pct == null ? "—" : `${positive ? "+" : ""}${totals.delta_pct}%`}
          icon={positive ? ArrowUpRight : ArrowDownRight}
          accent={
            positive
              ? "from-emerald-500/20 to-emerald-500/5 text-emerald-600"
              : "from-rose-500/20 to-rose-500/5 text-rose-600"
          }
        />
        <KpiCard
          testid="kpi-languages"
          label="Idiomas activos"
          value={by_language.filter((l) => l.key !== "(sin idioma)").length}
          icon={TrendingUp}
          accent="from-sky-500/20 to-sky-500/5 text-sky-600"
        />
        <KpiCard
          testid="kpi-sources"
          label="UTM sources"
          value={by_utm_source.filter((l) => l.key !== "(direct)").length}
          icon={TrendingDown}
          accent="from-amber-500/20 to-amber-500/5 text-amber-600"
        />
      </div>

      {/* Daily trend */}
      <Card title="Evolución diaria" testid="card-daily">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={daily_series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="atlasArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6B46C1" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#6B46C1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={shortDate} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <Tooltip contentStyle={tooltipStyle} labelFormatter={shortDate} />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#6B46C1"
              strokeWidth={2}
              fill="url(#atlasArea)"
              name="Leads"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Distribución por idioma" testid="card-language">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={by_language}
                  dataKey="count"
                  nameKey="key"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {by_language.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Top UTM sources" testid="card-utm-source">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={by_utm_source} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                <YAxis type="category" dataKey="key" tick={{ fontSize: 11, fill: "#475569" }} width={100} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#6B46C1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Top provincias" testid="card-provincia">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={by_provincia} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="key"
                  tick={{ fontSize: 10, fill: "#475569" }}
                  interval={0}
                  angle={-18}
                  textAnchor="end"
                  height={60}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Campañas UTM" testid="card-campaigns">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left py-2 font-semibold">Campaign</th>
                <th className="text-right py-2 font-semibold">Leads</th>
              </tr>
            </thead>
            <tbody data-testid="metrics-campaigns-tbody">
              {by_utm_campaign.map((c) => (
                <tr key={c.key} className="border-b border-slate-50 last:border-0">
                  <td className="py-2 text-slate-700">{c.key}</td>
                  <td className="py-2 text-right font-semibold">{c.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {by_utm_medium.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
                UTM medium
              </div>
              <div className="flex flex-wrap gap-2" data-testid="metrics-utm-medium">
                {by_utm_medium.map((m) => (
                  <Badge key={m.key} variant="secondary" className="font-normal">
                    {m.key} · <span className="font-semibold ml-1">{m.count}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white p-5 relative overflow-hidden`}
    >
      <div
        className={`absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br ${accent}`}
        aria-hidden
      />
      <div className="relative">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Icon size={18} className="text-slate-400" />
          <span className="font-display text-3xl font-extrabold">{value}</span>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="text-sm font-bold text-slate-700 mb-4">{title}</div>
      {children}
    </div>
  );
}

function AdminHeader({ current, onLogout }) {
  const navigate = useNavigate();
  const nav = [
    { to: "/admin/leads", label: "Leads", icon: Users, id: "leads" },
    { to: "/admin/metrics", label: "Métricas", icon: BarChart3, id: "metrics" },
  ];
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/admin/leads" className="flex items-center gap-3 shrink-0">
          <img
            src="/atlas-logo.png"
            alt="ATLAS Industrial"
            className="h-10 w-auto object-contain select-none"
            draggable={false}
          />
          <div className="pl-3 border-l border-slate-200">
            <div className="font-display text-sm font-bold uppercase tracking-[0.18em] text-slate-500 leading-none">
              Admin
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {current === "metrics" ? "Panel de Métricas" : "Panel de Leads"}
            </div>
          </div>
        </Link>
        <nav className="hidden sm:flex items-center gap-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = n.id === current;
            return (
              <button
                key={n.id}
                data-testid={`admin-nav-${n.id}`}
                onClick={() => navigate(n.to)}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-semibold transition-colors ${
                  active
                    ? "bg-[#6B46C1]/10 text-[#6B46C1]"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <Icon size={15} />
                {n.label}
              </button>
            );
          })}
        </nav>
        <Button
          data-testid="admin-logout-btn"
          variant="ghost"
          size="sm"
          onClick={onLogout}
        >
          <LogOut className="mr-1.5 h-4 w-4" />
          Salir
        </Button>
      </div>
    </header>
  );
}

function LoginCard({ form, setForm, onSubmit, loading }) {
  return (
    <div
      data-testid="admin-login-card"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center px-4"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#6B46C1]/10 flex items-center justify-center">
            <Lock className="text-[#6B46C1]" size={24} />
          </div>
          <div>
            <h1 className="font-display text-xl font-extrabold">ATLAS · Admin</h1>
            <p className="text-xs text-slate-500">Panel de Métricas</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="m-username">Usuario</Label>
            <Input
              id="m-username"
              data-testid="admin-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-password">Contraseña</Label>
            <Input
              id="m-password"
              data-testid="admin-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="current-password"
              required
            />
          </div>
        </div>
        <Button
          type="submit"
          data-testid="admin-login-btn"
          className="mt-6 w-full bg-[#6B46C1] hover:bg-[#553C9A] text-white font-semibold h-11"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ingresando...
            </>
          ) : (
            "Ingresar"
          )}
        </Button>
      </form>
    </div>
  );
}

const tooltipStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  fontSize: 12,
  boxShadow: "0 10px 30px -15px rgba(15,23,42,0.2)",
};

function shortDate(iso) {
  if (!iso) return iso;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
}
